<script setup>
import { AnnStore } from "@/front/stores/ann_store.js";
const annStore = AnnStore();
import { PlayerStore } from "@/front/stores/player_store.js";
const playerStore = PlayerStore();
import { PlaybackStore } from "@/front/stores/playback_store.js";
const playbackStore = PlaybackStore();
import { ClipStore } from "@/front/stores/clip_store.js";
const clipStore = ClipStore();
import { ClickStore } from "@/front/stores/click_store.js";
const clickStore = ClickStore();
import { VideoStore } from "@/front/stores/video_store.js";
const videoStore = VideoStore();
import LayerButton from "@/front/components/annotations/LayerButton.vue";
import LayerImage from "./annotations/LayerImage.vue";
import LayerHotspot from "./annotations/LayerHotspot.vue";
import LayerText from "./annotations/LayerText.vue";
import LayerClip from "./annotations/LayerClip.vue";
import LayerComment from "./annotations/LayerComment.vue";
import Button from "primevue/button";
import VueDraggableResizable from "vue-draggable-resizable";
import { clickInteraction, openMagicMenu, openGuide } from "@/front/composables/ClickComposer.js";
import { useGlobalRenderTracker } from '@/front/composables/GlobalRenderTracker.js'
if (playerStore.isDebugPerformance) {
  useGlobalRenderTracker('IntLayer', { logLevel: 'none' })
}

import { nextTick, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n({});

const props = defineProps({
  isMenu: {
    type: Boolean,
    default: false,
  },
  isEnd: {
    type: Boolean,
    default: false,
  },
});

const currentDimensions = ref({
  width: 0,
  height: 0,
  left: 0,
  top: 0,
  minHeight: 0,
});


const onDragStop = (x, y) => {
  annStore.editAnn.pos_left = pxToWidthPercent(x);
  annStore.editAnn.pos_top = pxToHeightPercent(y);
};

const onResizeCallback = (handle, x, y, width, height) => {
  currentDimensions.value = {
    width,
    height,
    left: x,
    top: y,
  };
  let wp = pxToWidthPercent(width)
  let hp = pxToHeightPercent(height)
  // log.info(3,"🔎 IntLayer onResizeCallback", wp, hp, annStore.editAnn.pos_width*annStore.editAnn.pos_height, wp*hp, annStore.editAnn.pos_width*annStore.editAnn.pos_height)
  // const fontRatio = Math.max(Math.min(wp * hp / (annStore.editAnn.pos_width * annStore.editAnn.pos_height), 1.05), 0.95);
  // log.info(3,"🔎 IntLayer fontRatio", fontRatio)
  // let oldDimensions = annStore.editAnn.pos_width * annStore.editAnn.pos_height
  // let newDimensions = wp * hp
  // let fontRatio = newDimensions / oldDimensions
  // log.info(3,"Old Dimensions", oldDimensions)
  // log.info(3,"New Dimensions", newDimensions)
  // log.info(3,"Font Ratio", fontRatio)
  // annStore.editAnn.font_size = Number(
  //   Math.min(Math.max(10, Math.round(annStore.editAnn.font_size * fontRatio).toFixed(1)), 80)
  // );
  // log.info(3,"🔎 IntLayer fontSize", annStore.editAnn.font_size)
  annStore.editAnn.pos_width = wp;
  annStore.editAnn.pos_height = hp;
};

const annList = computed(() => {
  let list;
  if (playerStore.branching) {
    return []
  }
  if (props.isEnd) {
    list = annStore.anns.filter(ann => ann.tray_type === "ctaTray" && ann.token == videoStore.video?.token);
  } else if (props.isMenu) {
    list = annStore.anns.filter(ann => ann.tray_type === "resourceTray" && ann.token == videoStore.video?.token);
  } else {
    list = Array.from(annStore.active.values()).filter(ann => ann.token == videoStore.video?.token);
  }

  if (annStore.isEditing && annStore.editAnn) {
    list = list.filter(ann =>
      // drop by id if set, otherwise drop by reference
      (ann.id != null ? ann.id !== annStore.editAnn.id : ann !== annStore.editAnn)
    );
  }

  // log.info(3,"🔎 IntLayer annList length", list.length)

  return list
  // return list.filter(ann => !ann.hidden && ann.visible);
});

const handleDoubleClick = (ann) => {
  log.info(3,"🔎 IntLayer handleDoubleClick", ann)
  if (playbackStore.manager) {
    clickNonEditingInteraction(ann, true);
  }
};

const isDisabled = (ann) => {
  return ann.post_click_state == "disabled" && ann.clicked
}

const isVisible = (ann) => {
  let result = ann.visible && !ann.hidden && !(ann.post_click_state == "hidden" && ann.clicked)
  // log.info(3,"🔎 IntLayer isVisible", ann.content, result)
  return result
}


const handleSingleClick = (ann) => {
  log.info(3,"🔎 IntLayer handleSingleClick", ann)
  if (isDisabled(ann)) {
    return
  }
  if (playbackStore.manager) {
    clickNonEditingInteraction(ann);
  } else {
    clickInteraction(ann);
  }
  setTimeout(() => {
    playerStore.player.controls.show(0, null);
    playerStore.player.controls.resume();
  }, 100);
};

const clickNonEditingInteraction = (ann, forceEdit = false) => {
  if (annStore.isBulkEditing) {
    if (annStore.bulkEditInteractions.includes(ann.id)) {
      annStore.bulkEditInteractions = annStore.bulkEditInteractions.filter(
        (id) => id !== ann.id
      );
    } else {
      annStore.bulkEditInteractions.push(ann.id);
    }
    return;
  }
  if (!annStore.isEditing) {
    if (forceEdit) {
      annStore.startEdit(ann.id);
    } else {
      clickInteraction(ann);
    }
    return;
  }
  annStore.updateAnn(annStore.editAnn).then(() => {
    annStore.startEdit(ann.id);
  });
};

const handleLayerClick = () => {
  if (props.isMenu || annStore.isBulkEditing || clipStore.src) {
    return;
  } else if (!videoStore.video.manage && videoStore.video.block_pausing && playerStore.playerState.playing) {
    return;
  } else {
    playerStore.togglePlayer("intLayer");
  }
};

const animations = {
  fadeLeft: { in: "animate__fadeInLeft", out: "animate__fadeOutLeft" },
  fadeRight: { in: "animate__fadeInRight", out: "animate__fadeOutRight" },
  rotate: { in: "animate__rotateIn", out: "animate__rotateOut" },
  zoom: { in: "animate__zoomIn", out: "animate__zoomOut" },
  fadeUp: { in: "animate__fadeInUp", out: "animate__fadeOutUp" },
  fadeDown: { in: "animate__fadeInDown", out: "animate__fadeOutDown" },
  zoomDown: { in: "animate__zoomInDown", out: "animate__zoomOutDown" },
  zoomLeft: { in: "animate__zoomInLeft", out: "animate__zoomOutLeft" },
  zoomRight: { in: "animate__zoomInRight", out: "animate__zoomOutRight" },
  zoomUp: { in: "animate__zoomInUp", out: "animate__zoomOutUp" },
  bounce: { in: "animate__bounceIn", out: "animate__bounceOut" },
  fade: { in: "animate__fadeIn", out: "animate__fadeOut" },
  bounceLeft: { in: "animate__bounceInLeft", out: "animate__bounceOutLeft" },
  bounceRight: { in: "animate__bounceInRight", out: "animate__bounceOutRight" },
  bounceUp: { in: "animate__bounceInUp", out: "animate__bounceOutUp" },
  bounceDown: { in: "animate__bounceInDown", out: "animate__bounceOutDown" },
  slideLeft: { in: "animate__slideInLeft", out: "animate__slideOutLeft" },
  slideRight: { in: "animate__slideInRight", out: "animate__slideOutRight" },
  slideUp: { in: "animate__slideInUp", out: "animate__slideOutUp" },
  slideDown: { in: "animate__slideInDown", out: "animate__slideOutDown" },
};

const getAnimationClass = (transition, isLeaving = false) => {
  const animation = animations[transition] || animations.fade;
  return isLeaving ? "animate__fadeOut" : animation.in;
};

const handleLeave = (el) => {
  // Get the current animation class
  const currentClass = Array.from(el.classList).find((cls) =>
    cls.startsWith("animate__")
  );
  if (!currentClass) return;

  // Find the transition type from the class
  const transitionType = Object.entries(animations).find(
    ([_, classes]) => classes.in === currentClass
  )?.[0];

  if (transitionType) {
    // Apply the corresponding leave animation
    el.classList.remove(currentClass);
    el.classList.add(getAnimationClass(transitionType, true));
  }
};
const isTrackableHotspot = computed(() => {
  try {
    return annStore.editAnn?.type_of == "hotspot" && annStore.editAnn?.points && annStore.editAnn?.points.length > 0
  } catch (e) {
    return false
  }
})

import { PositionCalculator } from "@/front/composables/PositionCalculator.js";
import { SearchList01Icon, MenuSquareIcon } from "hugeicons-vue";
import { localize } from "../composables/StringComposer";
const { calculatePosition, pxToWidthPercent, pxToHeightPercent } =
  PositionCalculator();

const getClass = (ann) => {
  return {
    "bulk-selected":
      annStore.isBulkEditing && annStore.bulkEditInteractions.includes(ann.id),
    "bulk-not-selected":
      annStore.isBulkEditing && !annStore.bulkEditInteractions.includes(ann.id),
  };
};

const onClipEnded = () => {
  clipStore.clear();
  playerStore.play("IntLayer onClipEnded");
};

const isImage = computed(() => annStore.editAnn?.type_of === "image");
const isClip = computed(() => annStore.editAnn?.type_of === "video");

const aspectRatio = computed(() => {
  // log.info(3,"🔎 IntLayer aspectRatio", isImage.value, isClip.value)
  if (isImage.value) return annStore.editAnn.aspect_ratio || null;
  if (isClip.value) return clipStore.aspectRatio || 1.77;
  return null;
});

const dimensions = computed(() => {
  if (!annStore.editAnn) return { width: 0, height: 0, left: 0, top: 0 };
  if (currentDimensions.value.width > 0) {
    return currentDimensions.value;
  }
  const pos = calculatePosition(annStore.editAnn);
  if ((isImage.value) && aspectRatio.value) {
    const width = pos.width;
    const height = width / aspectRatio.value;
    const newDimensions = { ...pos, width, height };
    currentDimensions.value = newDimensions;
    return newDimensions;
  }
  return pos;
});

const getGuideAndMMButtonVerticalStyling = computed(() => {
  // 20% of layer height
  let height = Math.min(playerStore.layerDimensions.height * 0.15, 70)
  height = Math.max(height, 40)
  return {
    top: 'calc(50% - ' + height/2 + 'px)',
    height: height + 'px',
    width: height + 'px',
    'border-top': '2px solid white',
    'border-right': '2px solid white',
  }
})

watch(
  () => annStore.editAnn,
  () => {
    log.info(3,"🔎 IntLayer watch annStore.editAnn", annStore.editAnn)
    currentDimensions.value = {
      width: 0,
      height: 0,
      left: 0,
      top: 0,
      minHeight: 0,
    };
  },
  { deep: true }
);

const visibleAnns = computed(() =>
  annList.value.filter(
    (ann) =>
      !ann.hidden && !(annStore.isEditing && annStore.editAnn.id === ann.id && ann.visible !== false)
  )
);


const magicMenuIconVisible = computed(() => {
  if (!videoStore.video.design) return false
  if (!videoStore.video.design.magic_menu) return false
  if (!videoStore.video.design.magic_menu.visible) return false
  if (!annStore.visibleMenuAnns.length) return false
  if (!playerStore.playerState.controlsVisible) return false
  if (props.isMenu) return false
  if (props.isEnd) return false
  return true
})

const guideIconVisible = computed(() => {  
  if (!videoStore.video.design) return false
  if (!playerStore.playerState.controlsVisible) return false
  if (props.isMenu) return false
  if (props.isEnd) return false

  return (videoStore.video.chapters?.length > 0 && videoStore.video.design.guide_chapters || annStore.anns.length > 0 && videoStore.video.design.guide_interactions || videoStore.video.transcript && videoStore.video.design.guide_transcript);
})

onMounted(() => {
  // log.info(3,"🔎 IntLayer onMounted")
  // log.info(3,"🔎 IntLayer onMounted magicMenuIconVisible", magicMenuIconVisible.value)
  // log.info(3,"🔎 IntLayer onMounted guideIconVisible", guideIconVisible.value)
})


const showPinnedChapters = computed(() => {
  if (!playerStore.started) return false;
  return videoStore.video.chapters?.length > 0 && videoStore.video.design?.static_chapters;
});

const clickChapter = (chapter) => {
  log.info(3,chapter);
  playerStore.seek(chapter.start);
};

const gridSize = computed(() => {
  if (!annStore.useGrid) return [1, 1]
  return [playerStore.layerDimensions.width / 20, playerStore.layerDimensions.height / 20]
})

const getImageTooltip = (ann) => {
  if (ann.type_of != "image") return null
  if (ann.click_action == 'genie') {
    return localize('open_genie_tooltip')
  } else if (ann.click_action == 'resourceTray') {
    return localize('open_magic_menu_tooltip')
  } 
  return false
}

</script>

<template>
  <div
    id="int-layer"
    :data-is-menu="isMenu"
    :data-is-end="isEnd"
    class="absolute overflow-y-scroll no-scrollbar w-full h-full left-0 top-0 select-none"
    @click.stop="handleLayerClick"
  >
    <TransitionGroup
      tag="div"
      class="ann-container z-10"
      :enter-active-class="`animate__animated`"
      :leave-active-class="`animate__animated`"
      :move-class="`transition-move`"
      @leave="handleLeave"
    >
      <div
        v-for="ann in annList"
        :key="ann.id"
        :class="[
          'interaction-content',
          getAnimationClass(ann.transition || 'fade'),
          getClass(ann),
        ]"
        class="z-10"
        @dblclick.stop="handleDoubleClick(ann)"
        @click.stop="handleSingleClick(ann)"
      >
        <LayerButton
          v-if="ann.type_of == 'button' && isVisible(ann) && !clickStore.questionActive"
          :ann="ann"
          :disabled="isDisabled(ann)"
          :is-menu="isMenu"
          :is-end="isEnd"
          class="absolute pointer-events-auto"
        />
        <LayerImage
          v-else-if="ann.type_of == 'image' && isVisible(ann)"
          :ann="ann"
          :disabled="isDisabled(ann)"
          v-tooltip="getImageTooltip(ann)"

          :is-menu="isMenu"
          :is-end="isEnd"
          class="absolute pointer-events-auto"
        />
        <LayerHotspot
          v-else-if="ann.type_of == 'hotspot' && isVisible(ann) && !clickStore.questionActive"
          :ann="ann"
          :disabled="isDisabled(ann)"
          :is-menu="isMenu"
          :is-end="isEnd"
          class="absolute pointer-events-auto"
        />
        <LayerText
          v-else-if="ann.type_of == 'text' && isVisible(ann)"
          :ann="ann"
          :disabled="isDisabled(ann)"
          :is-menu="isMenu"
          :is-end="isEnd"
          class="absolute pointer-events-auto"
        />
      </div>
    </TransitionGroup>

    <!-- Comments -->

    <TransitionGroup
      v-if="annStore.hasComments"
      tag="div"
      class="ann-container"
      :enter-active-class="`animate__animated`"
      :leave-active-class="`animate__animated`"
      :move-class="`transition-move`"
      @leave="handleLeave"
    >
      <div
        v-for="(ann, index) in annList.filter(ann => ann.type_of == 'comment' && isVisible(ann))"
        :key="ann.id"
        :class="[
          'interaction-content',
          getAnimationClass(ann.transition || 'fade'),
          getClass(ann),
        ]"
        @dblclick.stop="handleDoubleClick(ann)"
        @click.stop="handleSingleClick(ann)"
      >
        <LayerComment
          :index="index"
          :ann="ann"
          :disabled="isDisabled(ann)"
          :is-menu="isMenu"
          :is-end="isEnd"
          class="absolute pointer-events-auto"
        />
      </div>
    </TransitionGroup>

    <TransitionGroup name="fade" tag="button">
      <!-- Magic Menu -->
      <button
        v-if="magicMenuIconVisible && playerStore.started"
        v-tooltip.right="localize('show_magic_menu')"
        class="!absolute left-0 rounded-none rounded-tr-lg rounded-br-lg sm:rounded-tr-xl sm:rounded-br-xl !bg-[var(--user-theme-color)] border-2 border-white !border-l-0 !p-0"
        :style="getGuideAndMMButtonVerticalStyling"
        :class="{'spotlightDim': annStore.spotlightInteraction}"
        @click.stop="openMagicMenu"
      >
        <img v-if="videoStore.video.design.magic_menu.icon" :src="videoStore.video.design.magic_menu.icon" class="aspect-square mx-auto" />
        <MenuSquareIcon v-else class="w-[50%] h-[50%] aspect-square mx-auto" />
      </button>

      <!-- Guide -->
      <button
        v-if="guideIconVisible && playerStore.started"
        v-tooltip.left="localize('show_guide')"
        class="!absolute right-0 rounded-none rounded-tl-lg rounded-bl-lg sm:rounded-tl-xl sm:rounded-bl-xl !bg-[var(--user-theme-color)] border-2 border-white !border-r-0 !p-0"
        :style="getGuideAndMMButtonVerticalStyling"
        :class="{'spotlightDim': annStore.spotlightInteraction}"

        @click.stop="openGuide"
      >
        <SearchList01Icon class="w-[50%] h-[50%] aspect-square mx-auto text-white" />
      </button>

      <div v-if="showPinnedChapters" class="absolute top-2 left-2">
        <div v-for="chapter in videoStore.video.chapters" :key="chapter.id" class="text-white mb-3 p-0 text-lg text-left rounded-md px-2 py-0" @click.stop="clickChapter(chapter)">
          <span class="bg-black/80 rounded-md px-2 py-1.5">{{ chapter.title }}</span>
        </div>
      </div>
    </TransitionGroup>
    <div v-if="annStore.isEditing && annStore.useGrid" id="grid-overlay" class="absolute inset-0 pointer-events-none w-full h-full" >
      <!--  -->
      <div v-for="i in 10" :key="i" class="w-full h-[10%] flex border-b-2 opacity-50" :class="{ 'border-red-500': i % 2 === 0, 'border-yellow-500': i % 2 === 1 }" >  
        <div v-for="j in 10" :key="j" class="w-[10%] h-full border-l-2 opacity-50" :class="{ 'border-white-500': j % 2 === 0, 'border-white-500': j % 2 === 1 }" >
        </div>
      </div>
    </div>
    <vue-draggable-resizable
      v-if="
        annStore.isEditing &&
        annStore.editAnn &&
        annStore.needsPlacement &&
        !annStore.imageLoading
      "
      :w="dimensions.width"
      :h="dimensions.height"
      :x="dimensions.left"
      :y="dimensions.top"
      v-tooltip.bottom="isTrackableHotspot ? 'Trackable Hotspot: Edit tracking to adjust position, disable tracking to resize. ' : null"
      :lock-aspect-ratio="false && isImage && !!aspectRatio"
      :active="true"
      :prevent-deactivation="true"
      :handles="['tr', 'tl', 'br', 'bl', 'ml', 'mr']"
      :grid="gridSize"
      :parent="!annStore.useGrid"
      class="!z-10"
      @dragStop="onDragStop"
      :onResize="onResizeCallback"
      :draggable="!isTrackableHotspot"
      :resizable="!isTrackableHotspot"
      @mousedown="onMouseDown"
    >
      <LayerButton
        v-if="annStore.editAnn.type_of === 'button'"
        :ann="annStore.editAnn"
        :editing="true"
        class="pointer-events-none"
      />
      <LayerImage
        v-else-if="annStore.editAnn.type_of == 'image'"
        :ann="annStore.editAnn"
        :editing="true"
        class="pointer-events-none"
      />
      <LayerHotspot
        v-else-if="annStore.editAnn.type_of == 'hotspot'"
        :style="{ borderRadius: annStore.editAnn.hotspot_radius + '%' }"
        :ann="annStore.editAnn"
        :editing="true"
        class="bg-black/30 pointer-events-none"
      />
      <LayerText
        v-else-if="annStore.editAnn.type_of == 'text'"
        :ann="annStore.editAnn"
        :editing="true"
        class="pointer-events-none"
      />
      <LayerClip
        v-else-if="annStore.editAnn.type_of == 'video'"
        :ann="annStore.editAnn"
        :editing="true"
        class="pointer-events-none"
      />
    </vue-draggable-resizable>
  </div>
</template>

<style>
@import "vue-draggable-resizable/style.css";

.interaction-content {
  animation-duration: 0.5s;
  animation-fill-mode: both;
  transform-origin: center center;
  /* background: rgba(255,0,0,0.1); */
  pointer-events: none;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  position: absolute;
}

.transition-move {
  transition: transform 0.5s ease;
}

.bulk-not-selected {
  opacity: 0.5;
  filter: brightness(40%);
}

.grid-overlay {
  /* set your division counts */
  position: relative;
}
.grid-overlay::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none; /* so it doesn’t block clicks */

  background-image:
    /* vertical grid lines */
    repeating-linear-gradient(
      to right,
      yellow 0, yellow 1px,
      transparent 1px, transparent calc(100% / var(--cols))
    ),
    /* horizontal grid lines */
    repeating-linear-gradient(
      to bottom,
      yellow 0, yellow 1px,
      transparent 1px, transparent calc(100% / var(--rows))
    );
  background-size: 
    calc(100% / var(--cols)) calc(100% / var(--rows));
}
</style>